import {
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  DollarOutlined,
  UserOutlined,
  TeamOutlined,
  EyeOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import { Table, Button, message, Modal, Input, Tooltip, Form, Switch } from "antd";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatCurrency } from "../../../utils/formatters";
import { apiDelete } from "../../../utils/apiHelpers";
import { apiClient } from "../../../shared/api/AllApi";
import { invalidateCatalog, useCustomersCatalog } from "../../../hooks/useCatalogData";
import usePermissions from "../../../hooks/usePermissions";
import { ListPage, StatCard, T } from "../../../shared/ui/PageShell";
import { fixedStart } from "../../../i18";
import useTablePagination from "../../../shared/ui/useTablePagination";

const GetCustomer = () => {
  const { pagination, resetPage } = useTablePagination();
  const { hasPermission } = usePermissions();
  const canView = hasPermission("customers.index");
  const canAdd = hasPermission("customers.add");
  const canEdit = hasPermission("customers.edit");
  const canDelete = hasPermission("customers.delete");
  const canPayment = hasPermission("customers.payment");
  const canIncreaseDebt = hasPermission("maintenance.reset");
  const canSeeDebt = canPayment || canView;
  const [SearchText, setSearchText] = useState("");
  const { t, i18n } = useTranslation();
  const [dataSource, setDataSource] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const { data: customers, loading, refresh } = useCustomersCatalog();

  // Customer balances are live financial data. The shared catalog is cached for
  // dropdown performance, so always refresh it when this report/list page opens.
  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    setDataSource(
      customers.map((item) => ({
        id: item.id,
        name: item.name,
        email: item.email,
        total_owed: item.total_owed,
        phone_number: item.phone_number,
        is_pos_default: !!item.is_pos_default,
      })).sort((a, b) =>
        parseFloat(b.total_owed || 0) - parseFloat(a.total_owed || 0)
      )
    );
  }, [customers]);

  const handleDelete = (id) => { setDeleteItemId(id); setIsDeleteModalVisible(true); };

  const openAdd = () => {
    setEditingCustomer(null);
    form.resetFields();
    form.setFieldsValue({ is_pos_default: false });
    setFormOpen(true);
  };

  const openEdit = (customer) => {
    setEditingCustomer(customer);
    form.setFieldsValue({
      name: customer.name || "",
      phone_number: customer.phone_number || "",
      is_pos_default: !!customer.is_pos_default,
    });
    setFormOpen(true);
  };

  const saveCustomer = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        phone_number: values.phone_number?.trim() || null,
      };
      if (editingCustomer) {
        await apiClient.put(`/customers/${editingCustomer.id}`, payload);
      } else {
        await apiClient.post("/customers", payload);
      }
      invalidateCatalog("customers");
      await refresh();
      setFormOpen(false);
      setEditingCustomer(null);
      message.success(t("successMessage", "Form submitted successfully"));
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || t("errorMessage", "Failed to submit form. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmedDelete = async () => {
    try {
      if (!deleteItemId) { message.error(t("noItemSelected", "No item selected")); return; }
      await apiDelete(`customers/${deleteItemId}`);
      invalidateCatalog("customers");
      message.success(t("deleteSuccess", "Customer deleted successfully"));
      await refresh();
      setDeleteItemId(null);
      setIsDeleteModalVisible(false);
    } catch {
      message.error(t("deleteError", "Error deleting customer"));
    }
  };

  const totalDebt = dataSource.reduce((acc, item) => acc + parseFloat(item.total_owed || 0), 0);
  const debtors = dataSource.filter((i) => parseFloat(i.total_owed || 0) > 0).length;

  const columns = [
    {
      title: <b>{t("actions", "Actions")}</b>,
      key: "actions",
      align: "center",
      width: 210,
      fixed: fixedStart(i18n.language),
      render: (_, record) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
          {canView && (
            <Tooltip title={t("view", "View")}>
              <Link to={`/customers/${record.id}`}>
                <Button size="small" icon={<EyeOutlined />} style={{ borderRadius: 7, background: "#f1f5f9", borderColor: "#e2e8f0" }} />
              </Link>
            </Tooltip>
          )}
          {canIncreaseDebt && (
            <Tooltip title={t("increaseDebtTitle", "Increase Customer Debt")}>
              <Link to={`/customers/${record.id}/adjust-balance`}>
                <Button
                  size="small"
                  icon={<RiseOutlined />}
                  style={{ borderRadius: 7, background: "#fff7ed", borderColor: "#fed7aa", color: "#c2410c" }}
                />
              </Link>
            </Tooltip>
          )}
          {canPayment && (
            <Tooltip title={t("payDebt", "Pay Debt")}>
              <Link to={`/customers/${record.id}/pay`}>
                <Button
                  size="small"
                  icon={<DollarOutlined />}
                  style={{ borderRadius: 7, background: "#f0fdf4", borderColor: "#bbf7d0", color: "#16a34a" }}
                />
              </Link>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title={t("edit", "Edit")}>
              <Button type="primary" size="small" icon={<EditOutlined />} style={{ borderRadius: 7 }} onClick={() => openEdit(record)} />
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title={t("delete", "Delete")}>
              <Button danger size="small" icon={<DeleteOutlined />} style={{ borderRadius: 7 }} onClick={() => handleDelete(record.id)} />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => <span style={{ color: T.muted, fontSize: 13 }}>{index + 1}</span>,
      filteredValue: [SearchText],
      onFilter: (value, record) =>
        String(record.name).toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: <b>{t("name", "Name")}</b>,
      dataIndex: "name",
      key: "name",
      render: (text, record) => {
        const body = (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#e0f2fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <UserOutlined style={{ color: "#0284c7", fontSize: 14 }} />
            </div>
            <div>
              <div style={{ fontWeight: 600, color: T.text, fontSize: 14 }}>{text}</div>
              {record.email && <div style={{ fontSize: 12, color: T.muted }}>{record.email}</div>}
            </div>
          </div>
        );
        if (!canView) return body;
        return (
          <Link to={`/customers/${record.id}`} style={{ textDecoration: "none" }}>
            {body}
          </Link>
        );
      },
    },
    {
      title: <b>{t("phoneNumber", "Phone Number")}</b>,
      dataIndex: "phone_number",
      key: "phone_number",
      render: (text) => <span style={{ color: T.sub }}>{text || "-"}</span>,
    },
    canSeeDebt && {
      title: <b>{t("totalOwed", "Total Owed")}</b>,
      dataIndex: "total_owed",
      key: "total_owed",
      render: (val) => {
        const num = parseFloat(val || 0);
        return <span style={{ fontWeight: 700, color: num > 0 ? T.red : T.green }}>{formatCurrency(num)}</span>;
      },
    },
  ].filter(Boolean);

  const stats = [
    <StatCard key="total" icon={<TeamOutlined style={{ color: "#2563eb" }} />} label={t("customers", "Customers")} value={dataSource.length} iconBg="#dbeafe" />,
    canSeeDebt && <StatCard key="debt" icon={<DollarOutlined style={{ color: "#e11d48" }} />} label={t("totalOwed", "Total Owed")} value={formatCurrency(totalDebt)} iconBg="#fee2e2" valueColor={T.red} />,
    canSeeDebt && <StatCard key="debtors" icon={<UserOutlined style={{ color: "#d97706" }} />} label={t("debtors", "Debtors")} value={debtors} iconBg="#fef3c7" />,
  ].filter(Boolean);

  const toolbar = (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
      <Input.Search
        placeholder={t("searchHere", "Search here")}
        style={{ width: 280 }}
        allowClear
        onSearch={(v) => { setSearchText(v); resetPage(); }}
        onChange={(e) => { setSearchText(e.target.value); resetPage(); }}
      />
      <div style={{ flex: 1 }} />
      {canAdd && (
        <Button type="primary" icon={<PlusOutlined />} style={{ borderRadius: 9, fontWeight: 600 }} onClick={openAdd}>
          {t("addCustomer", "Add Customer")}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <ListPage
        breadcrumb={[t("sidebarSectionContacts", "Contacts"), t("customers", "Customers")]}
        title={t("customers", "Customers")}
        subtitle={`${dataSource.length} ${t("customers", "Customers").toLowerCase()}`}
        stats={stats}
        toolbar={toolbar}
      >
        <Table
          dataSource={dataSource.map((item) => ({ ...item, key: item.id }))}
          columns={columns}
          loading={loading}
          pagination={pagination}
          scroll={{ x: 700 }}
        />
      </ListPage>

      <Modal
        title={editingCustomer ? `${t("edit", "Edit")} ${t("customers", "Customers")}` : t("addCustomer", "Add Customer")}
        open={formOpen}
        onOk={saveCustomer}
        onCancel={() => setFormOpen(false)}
        okText={t("purchaseOrdersSave", "Save")}
        cancelText={t("cancel", "Cancel")}
        confirmLoading={saving}
        centered
        destroyOnHidden
        width={460}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 18 }}>
          <Form.Item
            name="name"
            label={t("name", "Name")}
            rules={[{ required: true, message: t("enterName", "Enter customer name") }]}
          >
            <Input placeholder={t("enterName", "Enter customer name")} />
          </Form.Item>
          <Form.Item name="phone_number" label={t("phone", "Phone Number")}>
            <Input placeholder={t("optional", "Optional")} />
          </Form.Item>
          <Form.Item
            name="is_pos_default"
            label={t("posDefaultCustomer", "Default POS Customer")}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("confirmDelete", "Confirm Delete")}
        open={isDeleteModalVisible}
        onOk={handleConfirmedDelete}
        onCancel={() => setIsDeleteModalVisible(false)}
        okText={t("yesDelete", "Yes, Delete")}
        cancelText={t("cancel", "Cancel")}
        centered
      >
        <p>{t("deleteConfirmation", "Are you sure you want to delete this customer?")}</p>
      </Modal>
    </>
  );
};

export default GetCustomer;
